/**
 * Non-blocking prefetch for /orders/family/day.
 *
 * The page's data chain is family → (active menus ∥ week orders) → per-mealtime
 * queries, with a React render between each hop — on a cold deep link that's
 * three serialized round-trips before anything paints. This loader kicks the
 * first two hops at NAVIGATION time and returns null immediately, so render
 * behavior (skeletons, guards) is unchanged; the hooks just find their data
 * in-flight or already cached.
 *
 * Query keys/fns are the hooks' own exported builders — never restate a key
 * here, or the prefetch silently stops matching.
 */

import type { LoaderFunctionArgs } from 'react-router'

import { fetchMyFamily, myFamilyQueryKey } from '@/api/family'
import { familyOrdersQueryKey, fetchFamilyOrders } from '@/api/family-orders'
import { fetchLocationActiveMenus, locationActiveMenusQueryKey, MENUS_STALE_TIME_MS } from '@/api/orders'
import { listOrderingStudents } from '@/api/orders-family'
import { serviceDateIso } from '@/api/school-orders'
import { groupStudentsByLocation } from '@/components/orders/family/week/columns'
import { queryClient } from '@/lib/query-client'
import { mondayOf } from '@/lib/school-orders/calendar'

export function familyDayLoader({ request }: LoaderFunctionArgs): null {
  const date = new URL(request.url).searchParams.get('date')
  void (async () => {
    try {
      const family = await queryClient.ensureQueryData({
        queryKey: myFamilyQueryKey,
        queryFn: ({ signal }: { signal?: AbortSignal }) => fetchMyFamily(signal),
      })
      if (!date) {
        return
      }
      const locations = groupStudentsByLocation(listOrderingStudents(family))
      const isoDate = serviceDateIso(date)
      for (const location of locations) {
        // Mirrors the page's useLocationActiveMenusQuery args exactly.
        const args = { locationId: location.id, activeOn: isoDate, withAllergens: true }
        void queryClient.prefetchQuery({
          queryKey: locationActiveMenusQueryKey(args),
          queryFn: ({ signal }) => fetchLocationActiveMenus(args, signal),
          staleTime: MENUS_STALE_TIME_MS,
        })
      }
      const ordersVars = { weekStart: mondayOf(date) }
      void queryClient.prefetchQuery({
        queryKey: familyOrdersQueryKey(ordersVars),
        queryFn: ({ signal }) => fetchFamilyOrders(ordersVars, signal),
      })
    } catch {
      // Prefetch is best-effort: any failure here surfaces (with retries and
      // real error states) through the page's own hooks.
    }
  })()
  return null
}
